import { OTHER_SPECIALTY_ID } from '@/components/ui/select/clinician-type-select';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { type ClinicianTypeInput } from '@/lib/types';
import { useMemo } from 'react';

import { useOnboardingContext } from '../context/OnboardingContext';

/**
 * Canonical (English) name of the currently-selected onboarding specialty,
 * resolved by id from the untranslated specialty list.
 *
 * The `/onboarding-v2-assets` endpoint resolves specialty content by the English
 * specialty name (slug + alias map), so callers must send the canonical name —
 * not `formData.specialty.name`, which follows the display language now that the
 * picker is localized (e.g. it could be "Andrologue" in French). Returns
 * `undefined` when no specialty is selected (keeps the asset query disabled), and
 * falls back to the stored name for the free-text "Other" option or before the
 * specialty list has loaded.
 */
export function useSelectedSpecialtyCanonicalName(): string | undefined {
  const { formData } = useOnboardingContext();
  return useSpecialtyCanonicalName(formData.specialty);
}

/**
 * Same resolution as `useSelectedSpecialtyCanonicalName`, but for callers that
 * run *before* `OnboardingContext` exists — `usePersonalizationForm` builds the
 * value the context later exposes, so it has to pass the specialty in.
 */
export function useSpecialtyCanonicalName(
  specialty: ClinicianTypeInput | null
): string | undefined {
  const { data: rawSpecialties } = useQuerySpecialties((d) => d.specialties);
  const specialtyId = specialty?.id;

  return useMemo(() => {
    if (!specialty) return undefined;
    if (specialtyId === OTHER_SPECIALTY_ID) return specialty.name;
    return rawSpecialties?.find((s) => s.id === specialtyId)?.name ?? specialty.name;
  }, [specialty, specialtyId, rawSpecialties]);
}
